/**
 * The item's own name block — and, when the set can drill, the item's ONE
 * control.
 *
 * The old open affordance wrapped the entire card in a `role="button"`, which
 * put an interactive ancestor above every action, gallery, player and
 * disclosure inside it, carried no `aria-expanded`, and needed a hand-kept
 * list of nested tags to avoid swallowing their clicks. Here the title itself
 * is the button: it carries the accessible name, `aria-expanded`,
 * `aria-controls` and the Enter/Space model, and it is the item's only tab
 * stop. Everything around it is a quiet click zone with no role at all.
 *
 * `truncationOrder` is required because only the caller knows which field may
 * yield. A component that guesses picks the title, every time, on the surface
 * where the title is the one thing a reader is scanning for.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import { useCresultcardFirstWordFloor } from "./cresultcard.first-word-floor";
import { useCrcTouchExpansion } from "./cresultcard.touch";
import {
  CRC_TEXT_MUTED,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardEyebrowVoice,
  CresultcardIdentitySlot,
  CresultcardTextStep,
  CresultcardTitleLines,
  type CresultcardOpenControl,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";
import { CresultcardTitle } from "./CresultcardTitle";

/** The title is the item's one control, and the kit stamps it as such. */
const TITLE_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Identity/TitleTrigger",
  "data-crc-control": true,
  "data-crc-title": true,
};
const TITLE_ROOT_ROLE = "button";
/**
 * The title is read down a column, so its surface starts on the leading edge.
 * These reach `Ccontainer` as props — a `textAlign` in `containerStyle` cannot
 * beat them, which is the same trap as the radius corollary one property over.
 */
const TITLE_ALIGN_ITEMS = "flex-start";
const TITLE_JUSTIFY_CONTENT = "flex-start";
/** Captured text keeps the case the vendor sent, against the step's own uppercase. */
const EYEBROW_CAPTURED_TRANSFORM: CSSProperties["textTransform"] = "none";

const TITLE_BUTTON_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  minWidth: 0,
  textAlign: "start",
};

interface CresultcardIdentityBaseProps {
  readonly mark?: ReactNode;
  readonly eyebrow?: ReactNode;
  /** Uppercased as a kit label unless the caller says the text was captured. */
  readonly eyebrowVoice?: CresultcardEyebrowVoice;
  readonly title: string;
  readonly secondary?: ReactNode;
  readonly trailing?: ReactNode;
  readonly titleLines: CresultcardTitleLines;
  /** Earlier slots yield first; later slots hold their intrinsic width. */
  readonly truncationOrder: readonly CresultcardIdentitySlot[];
}

/**
 * The title is the item's one control, and there are two things it can be: the
 * control that opens the item HERE, or a destination that leaves for the
 * vendor. Never both — an item cannot have two primary actions on one string,
 * and a caller holding both has a decision to make rather than a precedence
 * rule to learn. The union is what makes that a compile error; existing callers
 * pass `openControl` alone and are unaffected.
 */
interface CresultcardIdentityOpenProps extends CresultcardIdentityBaseProps {
  /** Present when this item opens something. Then the title IS the control. */
  readonly openControl?: CresultcardOpenControl;
  readonly href?: never;
}

/**
 * The item's title IS the destination. It renders a real anchor with a real
 * link menu — middle-click, open-in-new-tab, copy-link-address — which a
 * `role="button"` div simulating navigation gives a reader none of.
 */
interface CresultcardIdentityHrefProps extends CresultcardIdentityBaseProps {
  readonly openControl?: never;
  readonly href: string;
}

type CresultcardIdentityProps =
  CresultcardIdentityOpenProps | CresultcardIdentityHrefProps;

/** A slot that yields shrinks and clips; a slot that does not holds its width. */
function slotStyle(
  slot: CresultcardIdentitySlot,
  order: readonly CresultcardIdentitySlot[],
): CSSProperties {
  return order.includes(slot)
    ? { ...CRC_TRUNCATE_STYLE, flex: "1 1 auto" }
    : { flex: "0 0 auto", minWidth: 0 };
}

interface TitleSlotStyleInput {
  readonly order: readonly CresultcardIdentitySlot[];
  readonly titleLines: CresultcardTitleLines;
}

/**
 * The title yields like any other slot when the caller names it — the reason
 * naming it has to do something is that on a Row the title is the widest thing
 * on the line, and a caller who declares it as the field that gives up room is
 * making the only decision that can actually free any.
 *
 * A one-line title clips with an ellipsis; a multi-line title is already
 * clamped by `CresultcardTitle`, so it takes the shrink and the overflow guard
 * without the `nowrap` that would fight its own clamp.
 */
function titleSlotStyle({
  order,
  titleLines,
}: TitleSlotStyleInput): CSSProperties {
  if (!order.includes(CresultcardIdentitySlot.Title)) {
    return { flex: "0 0 auto", minWidth: 0 };
  }
  return titleLines === CresultcardTitleLines.One
    ? { ...CRC_TRUNCATE_STYLE, flex: "1 1 auto" }
    : { flex: "1 1 auto", minWidth: 0, overflow: "hidden" };
}

export function CresultcardIdentity({
  mark,
  eyebrow,
  eyebrowVoice = CresultcardEyebrowVoice.Label,
  title,
  secondary,
  trailing,
  titleLines,
  truncationOrder,
  openControl,
  href,
}: CresultcardIdentityProps): ReactElement {
  const radius = useCresultcardNestedRadius();
  const { isMobile } = useCresultcardViewport();
  const {
    first: eyebrowFirst,
    rest: eyebrowRest,
    firstRef: eyebrowFirstRef,
    floor: firstWordFloor,
  } = useCresultcardFirstWordFloor(eyebrow ?? "");
  const titleTouch = useCrcTouchExpansion({ isMobile });
  const titleStyle = titleSlotStyle({ order: truncationOrder, titleLines });
  /**
   * SPEC §7.4's minimum target, taken on the semantic root so the visible
   * title, its hover fill and its press flash all stay exactly where they are.
   * Nothing the expansion reaches over is interactive: the eyebrow above and
   * the secondary below are spans, and the identity block around them is
   * already a quiet click zone by design.
   *
   * The band is measured, not declared. A title is the one control here that
   * WRAPS, so its block size is not a property of its type step at all: a
   * declared 18.3 against a title that had gone to two lines and 36.59px bought
   * 18.3px of padding it did not need, and that band covered the whole secondary
   * line under it.
   */
  const titleRootStyle: CSSProperties = {
    ...TITLE_BUTTON_STYLE,
    ...titleStyle,
    ...titleTouch.style,
  };
  const titleSurfaceStyle: CSSProperties = {
    ...TITLE_BUTTON_STYLE,
    ...titleStyle,
  };

  const titleNode = (
    <CresultcardTitle lines={titleLines}>
      {title}
    </CresultcardTitle>
  );

  return (
    <div
      data-el="Cresultcard/Identity/Root"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 9,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {mark}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          minWidth: eyebrow == null ? 0 : firstWordFloor,
          flex: "1 1 auto",
        }}
      >
        {eyebrow != null && (
          <span
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
              ...slotStyle(CresultcardIdentitySlot.Eyebrow, truncationOrder),
              textTransform:
                eyebrowVoice === CresultcardEyebrowVoice.Captured
                  ? EYEBROW_CAPTURED_TRANSFORM
                  : undefined,
            }}
          >
            <span ref={eyebrowFirstRef} style={{ whiteSpace: "nowrap" }}>
              {eyebrowFirst}
            </span>
            {eyebrowRest}
          </span>
        )}
        {openControl != null ? (
          <Cbutton
            alignItems={TITLE_ALIGN_ITEMS}
            ariaLabel={title}
            composite
            containerStyle={titleSurfaceStyle}
            contentStyle={titleSurfaceStyle}
            disableClickMotion
            disableHoverEffects
            justifyContent={TITLE_JUSTIFY_CONTENT}
            onClick={openControl.onOpen}
            padding={0}
            preset={ButtonPreset.Ghost}
            radius={radius}
            rootAttributes={{
              ...TITLE_ROOT_ATTRIBUTES,
              "aria-controls": openControl.paneId,
              "aria-expanded": openControl.expanded,
            }}
            rootRef={titleTouch.measureRoot}
            rootRole={TITLE_ROOT_ROLE}
            rootTabIndex={0}
            style={titleRootStyle}
          >
            {titleNode}
          </Cbutton>
        ) : href != null ? (
          <Cbutton
            alignItems={TITLE_ALIGN_ITEMS}
            anchorRootRef={titleTouch.measureRoot}
            ariaLabel={title}
            containerStyle={titleSurfaceStyle}
            contentStyle={titleSurfaceStyle}
            disableClickMotion
            disableHoverEffects
            href={href}
            justifyContent={TITLE_JUSTIFY_CONTENT}
            padding={0}
            preset={ButtonPreset.Ghost}
            radius={radius}
            rootAttributes={{
              ...TITLE_ROOT_ATTRIBUTES,
            }}
            style={titleRootStyle}
          >
            {titleNode}
          </Cbutton>
        ) : (
          <div
            style={titleStyle}
          >
            {titleNode}
          </div>
        )}
        {secondary != null && (
          <span
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
              ...slotStyle(CresultcardIdentitySlot.Secondary, truncationOrder),
              color: CRC_TEXT_MUTED,
            }}
          >
            {secondary}
          </span>
        )}
      </div>
      {trailing != null && (
        <div
          data-el="Cresultcard/Identity/Trailing"
          style={slotStyle(CresultcardIdentitySlot.Trailing, truncationOrder)}
        >
          {trailing}
        </div>
      )}
    </div>
  );
}
